import React, { useCallback, useState } from "react";
import {
  FaComments,
  FaDownload,
  FaFileArchive,
  FaFileExcel,
  FaClipboardList,
  FaUserTimes,
  FaPoll,
  FaChartBar,
  FaChalkboardTeacher,
  FaHandshake,
  FaSpinner,
  FaLayerGroup,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../services/axios";
import "./admPatrocinadoresList.css";
import "./admRelatorios.css";

const RELATORIOS = [
  {
    id: "todos-zip",
    titulo: "Todos os relatórios",
    descricao:
      "Arquivo único compactado com todos os relatórios da listagem, organizados em pastas.",
    endpoint: "/relatorios-todos-zip",
    tipo: "zip",
    Icon: FaLayerGroup,
    destaque: true,
  },
  {
    id: "dados-interacao",
    titulo: "Dados interação",
    descricao:
      "Totais de postagens, curtidas, comentários, marcações Gostei e chats por usuário com total de mensagens.",
    endpoint: "/relatorios-dados-interacao",
    tipo: "xlsx",
    Icon: FaComments,
  },
  {
    id: "noshows",
    titulo: "No shows",
    descricao:
      "Lista de patrocinadores com reuniões marcadas como no show, incluindo convidado e horário.",
    endpoint: "/relatorios-noshows",
    tipo: "xlsx",
    Icon: FaUserTimes,
  },
  {
    id: "respostas-pesquisa",
    titulo: "Respostas pesquisa",
    descricao:
      "Reuniões de negócio ordenadas por patrocinador, com respostas e aba consolidada de percentuais e comparação.",
    endpoint: "/relatorios-respostas-pesquisa",
    tipo: "xlsx",
    Icon: FaPoll,
  },
  {
    id: "resumo-reunioes",
    titulo: "Resumo reuniões",
    descricao:
      "Quadro geral e blocos por patrocinador, com aba M&M (convidado, status, check-in e respostas).",
    endpoint: "/relatorios-resumo-reunioes",
    tipo: "xlsx",
    Icon: FaChartBar,
  },
  {
    id: "dados-conteudos",
    titulo: "Dados conteúdos",
    descricao:
      "Arquivo compactado com uma planilha por sala de conteúdo (entrada, nota, status e executivo).",
    endpoint: "/relatorios-dados-conteudos",
    tipo: "zip",
    Icon: FaChalkboardTeacher,
  },
  {
    id: "reunioes-patrocinadores",
    titulo: "Reuniões patrocinadores",
    descricao:
      "Arquivo compactado com uma planilha por patrocinador (status, convidado e resultados).",
    endpoint: "/relatorios-reunioes-patrocinadores",
    tipo: "zip",
    Icon: FaHandshake,
  },
];

async function mensagemErroBlob(err, fallback) {
  let mensagem = fallback;
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const texto = await data.text();
      const json = JSON.parse(texto);
      mensagem = json?.mensagem || mensagem;
    } catch {
      /* mantém fallback */
    }
  } else if (data?.mensagem) {
    mensagem = data.mensagem;
  }
  return mensagem;
}

function baixarBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const AdmRelatorios = () => {
  const [baixandoId, setBaixandoId] = useState(null);

  const baixarRelatorio = useCallback(async (relatorio) => {
    setBaixandoId(relatorio.id);
    try {
      const resp = await api.get(relatorio.endpoint, {
        responseType: "blob",
        timeout: 600000,
      });

      const disposition = resp.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(disposition);
      const extensao = relatorio.tipo === "zip" ? "zip" : "xlsx";
      const fileName =
        match?.[1] ||
        `${relatorio.id}_${new Date().toISOString().slice(0, 10)}.${extensao}`;

      const contentType =
        resp.headers?.["content-type"] ||
        (relatorio.tipo === "zip"
          ? "application/zip"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");

      const blob = new Blob([resp.data], { type: contentType });
      baixarBlob(blob, fileName);
      toast.success(`${relatorio.titulo} baixado com sucesso.`);
    } catch (err) {
      console.error("Erro ao baixar relatório:", err);
      const mensagem = await mensagemErroBlob(
        err,
        `Erro ao baixar ${relatorio.titulo}. Tente novamente.`,
      );
      toast.error(mensagem);
    } finally {
      setBaixandoId(null);
    }
  }, []);

  return (
    <div className="apl-page apl-page--padded adm-relatorios-page">
      <header className="apl-header" aria-label="Cabeçalho de relatórios">
        <div className="apl-header-text">
          <h1>Relatórios</h1>
          <p>
            Gere e baixe planilhas Excel (ou arquivos compactados) com os
            indicadores operacionais do evento.
          </p>
        </div>
      </header>

      <section className="adm-relatorios-lista" aria-label="Lista de relatórios">
        {RELATORIOS.map((relatorio) => {
          const Icon = relatorio.Icon;
          const baixando = baixandoId === relatorio.id;
          const TipoIcon =
            relatorio.tipo === "zip" ? FaFileArchive : FaFileExcel;

          return (
            <article
              key={relatorio.id}
              className={`adm-relatorios-card${
                relatorio.destaque ? " adm-relatorios-card--destaque" : ""
              }`}
            >
              <div className="adm-relatorios-card-icon" aria-hidden>
                <Icon />
              </div>
              <div className="adm-relatorios-card-body">
                <div className="adm-relatorios-card-top">
                  <h2>{relatorio.titulo}</h2>
                  <span className="adm-relatorios-badge">
                    <TipoIcon aria-hidden />
                    {relatorio.tipo === "zip" ? "ZIP + Excel" : "Excel"}
                  </span>
                </div>
                <p>{relatorio.descricao}</p>
              </div>
              <button
                type="button"
                className="apl-btn apl-btn--primary adm-relatorios-btn"
                onClick={() => baixarRelatorio(relatorio)}
                disabled={baixandoId != null}
                aria-busy={baixando}
              >
                {baixando ? (
                  <>
                    <FaSpinner className="adm-relatorios-spin" aria-hidden />
                    Gerando...
                  </>
                ) : (
                  <>
                    <FaDownload aria-hidden />
                    Baixar
                  </>
                )}
              </button>
            </article>
          );
        })}
      </section>

      <p className="adm-relatorios-hint">
        <FaClipboardList aria-hidden />
        A geração pode levar alguns segundos em bases grandes. O pacote completo
        demora mais — mantenha a sessão aberta até o download iniciar.
      </p>
    </div>
  );
};

export default AdmRelatorios;
